import React, { useEffect, useState, useCallback } from "react";
import styles from "./DataTable.module.css";
import Pagination from "./Pagination";
import { salesListing } from "../../../Bookclosure/Services/ApiHandler";
import ErrorPopup from "../../elements/ErrorPopup";

const DataTable = ({ SaleID, filters = [], onNoDataFound, refreshKey }) => {
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [totalPages, setTotalPages] = useState(1);
  const [errorPopup, setErrorPopup] = useState({
    isOpen: false,
    title: "No Data Found",
    message: "",
  });

  const formatHeading = (text) =>
    text
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .trim();


  useEffect(() => {
    setActiveFilters(filters);
    setCurrentPage(1);
  }, [filters]);

  
  const loadData = useCallback(async () => {
    if (!SaleID) {
      setError("SaleID is missing");
      setData([]);
      setColumns([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await salesListing(SaleID, {
        page: currentPage,
        pageSize: rowsPerPage,
        filters: activeFilters,
      });

      const res = response?.data || {};
      const fetchedData = res.saleListing || [];

      let headers = res.saleListingHeaders || [];
      if (headers.length === 0 && fetchedData.length > 0) {
        headers = Object.keys(fetchedData[0] || {});
      }

      setColumns(headers);
      setData(fetchedData);

      const totalPagesFromBackend = Number(res.totalPages || res.totalpages || 0);
      const computedTotalPages =
        totalPagesFromBackend > 0
          ? totalPagesFromBackend
          : Math.max(1, Math.ceil((fetchedData.length || 0) / rowsPerPage));

      setTotalPages(computedTotalPages);

      if (fetchedData.length === 0) {
        const message = "No sales records found for the selected criteria.";
        setError(message);

        if (activeFilters.length > 0) {
          setErrorPopup({
            isOpen: true,
            title: "No Data Found",
            message:
              "Please check if you entered the correct values for the selected filter.",
          });
        }
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load sales data. Please try again.");
      setData([]);
      setColumns([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [SaleID, currentPage, rowsPerPage, activeFilters]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.tableContainer}>
        {loading ? (
          <div style={{ padding: 20, textAlign: "center" }}>Loading sales data...</div>
        ) : error ? (
          <div style={{ padding: 20, textAlign: "center", color: "red" }}>{error}</div>
        ) : columns.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center" }}>No sales columns available.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col}>{formatHeading(col)}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {(data.length > rowsPerPage
                ? data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                : data
              ).map((row, i) => (
                <tr key={row.id || i}>
                  {columns.map((col) => (
                    <td key={col}>{row[col] ?? "-"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.paginationWrapper}>
        <Pagination
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(rows) => {
            setRowsPerPage(rows);
            setCurrentPage(1);
          }}
        />
      </div>

      <ErrorPopup
        isOpen={errorPopup.isOpen}
        title={errorPopup.title}
        message={errorPopup.message}
        onClose={() => {
          setErrorPopup({
            isOpen: false,
            title: "No Data Found",
            message: "",
          });
          if (typeof onNoDataFound === "function") {
            onNoDataFound();
          }
        }}
      />
    </div>
  );
};

export default DataTable;
